import React, { useMemo, useRef, useEffect, useCallback, useState } from 'react';
import { Box, Typography, Stack } from 'omaha-design-system';
import type { ScoreTimelinePoint } from './types';
import { ROTATION_COLORS } from './constants';

// Inline styles for DOM-manipulated elements (not MUI sx)
const crosshairStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: 1,
  pointerEvents: 'none',
  zIndex: 1,
  visibility: 'hidden',
  backgroundImage: 'repeating-linear-gradient(to bottom, #9CA3AF 0, #9CA3AF 3px, transparent 3px, transparent 6px)',
};

const tooltipStyle: React.CSSProperties = {
  position: 'absolute',
  top: 8,
  transform: 'translateX(-50%)',
  backgroundColor: 'rgba(26, 29, 36, 0.95)',
  border: `1px solid ${ROTATION_COLORS.border.default}`,
  borderRadius: 6,
  padding: '6px 10px',
  fontSize: '0.75rem',
  color: ROTATION_COLORS.text.primary,
  pointerEvents: 'none',
  zIndex: 2,
  whiteSpace: 'nowrap',
  visibility: 'hidden',
};

/** Generate a smooth Catmull-Rom SVG path through the given points. */
function smoothPathD(points: { x: number; y: number }[]): string {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x},${points[0].y}`;
  if (points.length === 2) {
    return `M ${points[0].x},${points[0].y} L ${points[1].x},${points[1].y}`;
  }

  let d = `M ${points[0].x},${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];

    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;

    d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }

  return d;
}

interface ScoreRibbonProps {
  data: ScoreTimelinePoint[];
  homeTeamColor: string;
  awayTeamColor: string;
  gameDuration: number;
  homeAbbrev: string;
  awayAbbrev: string;
}

const ScoreRibbon: React.FC<ScoreRibbonProps> = ({
  data,
  homeTeamColor,
  awayTeamColor,
  gameDuration,
  homeAbbrev,
  awayAbbrev,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const crosshairRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });

  // Measure wrapper div's inner dimensions
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const update = () => {
      setDims({ w: el.clientWidth, h: el.clientHeight });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Negate diff so positive = away leading (matches layout with away team on top)
  const chartData = useMemo(() => {
    const flipped = data.map((p) => ({ x: p.time, y: -p.scoreDiff }));
    if (flipped.length <= 200) return flipped;
    const step = Math.ceil(flipped.length / 200);
    return flipped.filter((_, idx) => idx % step === 0);
  }, [data]);

  // Keep refs in sync for use in mouse handler (avoids stale closures)
  const chartDataRef = useRef(chartData);
  chartDataRef.current = chartData;
  const dimsRef = useRef(dims);
  dimsRef.current = dims;

  const maxDiff = useMemo(() => {
    const max = Math.max(...data.map((p) => Math.abs(p.scoreDiff)), 10);
    return Math.ceil(max / 5) * 5;
  }, [data]);

  const { homeLargestLead, awayLargestLead } = useMemo(() => {
    let awayMax = 0;
    let homeMax = 0;
    for (const point of data) {
      if (-point.scoreDiff > awayMax) awayMax = -point.scoreDiff;
      if (point.scoreDiff > homeMax) homeMax = point.scoreDiff;
    }
    return {
      awayLargestLead: awayMax > 0 ? awayMax : null,
      homeLargestLead: homeMax > 0 ? homeMax : null,
    };
  }, [data]);

  const { w, h } = dims;
  const cy = h / 2;

  const svgPoints = useMemo(() => {
    if (w === 0 || h === 0) return [];
    return chartData.map((d) => ({
      x: (d.x / gameDuration) * w,
      y: cy - (d.y / maxDiff) * cy,
    }));
  }, [chartData, w, h, cy, gameDuration, maxDiff]);

  const { strokePath, areaPath } = useMemo(() => {
    if (svgPoints.length < 2) return { strokePath: '', areaPath: '' };
    const stroke = smoothPathD(svgPoints);
    const first = svgPoints[0];
    const last = svgPoints[svgPoints.length - 1];
    const area = `${stroke} L ${last.x},${cy} L ${first.x},${cy} Z`;
    return { strokePath: stroke, areaPath: area };
  }, [svgPoints, cy]);

  const gridYPositions = useMemo(() => [cy * 0.5, cy * 1.5], [cy]);

  // Direct DOM manipulation — no React state, no render cycle, zero lag
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const crosshair = crosshairRef.current;
    const tooltip = tooltipRef.current;
    if (!crosshair || !tooltip) return;

    const cw = e.currentTarget.clientWidth;
    const x = Math.max(0, Math.min(e.nativeEvent.offsetX, cw));

    crosshair.style.left = `${x}px`;
    crosshair.style.visibility = 'visible';

    const cd = chartDataRef.current;
    if (cw === 0 || cd.length === 0) return;

    const dataX = (x / cw) * gameDuration;
    let closest = 0;
    let closestDist = Infinity;
    for (let i = 0; i < cd.length; i++) {
      const dist = Math.abs(cd[i].x - dataX);
      if (dist < closestDist) {
        closestDist = dist;
        closest = i;
      }
    }

    const d = cd[closest];
    const period = Math.floor(d.x / 720) + 1;
    const remaining = 720 - (d.x % 720);
    const mins = Math.floor(remaining / 60);
    const secs = Math.round(remaining % 60);
    const timeLabel = `Q${period} ${mins}:${secs.toString().padStart(2, '0')}`;
    const diff = d.y;

    if (diff === 0) {
      tooltip.innerHTML =
        `<div style="font-weight:600">${timeLabel}</div>` +
        `<div style="color:#8B95A5">Tied</div>`;
    } else {
      const team = diff > 0 ? awayAbbrev : homeAbbrev;
      const color = diff > 0 ? awayTeamColor : homeTeamColor;
      const absDiff = Math.abs(Math.round(diff));
      tooltip.innerHTML =
        `<div style="font-weight:600;margin-bottom:4px">${timeLabel}</div>` +
        `<div style="display:flex;align-items:center;gap:6px">` +
        `<span style="width:8px;height:8px;border-radius:50%;background:${color};display:inline-block"></span>` +
        `<b>${team} +${absDiff}</b></div>`;
    }

    const tooltipX = Math.max(50, Math.min(x, cw - 50));
    tooltip.style.left = `${tooltipX}px`;
    tooltip.style.visibility = 'visible';
  }, [gameDuration, awayAbbrev, homeAbbrev, awayTeamColor, homeTeamColor]);

  const handleMouseLeave = useCallback(() => {
    if (crosshairRef.current) crosshairRef.current.style.visibility = 'hidden';
    if (tooltipRef.current) tooltipRef.current.style.visibility = 'hidden';
  }, []);

  return (
    <Box sx={{ m: '16px 8px' }}>
      <Stack
        direction="row"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 0.5,
          fontSize: '0.65rem',
          fontWeight: 500,
        }}
        style={{ marginBottom: 2 }}
      >
        <Typography style={{ fontSize: 'inherit', fontWeight: 'inherit', color: awayTeamColor }}>
          {awayAbbrev} lead
        </Typography>
        {awayLargestLead && (
          <Typography sx={{ color: ROTATION_COLORS.text.secondary, fontSize: 'inherit' }}>
            max +{awayLargestLead}
          </Typography>
        )}
      </Stack>

      <Box
        ref={wrapperRef}
        sx={{
          position: 'relative',
          height: 144,
          backgroundColor: 'rgba(26, 29, 36, 0.5)',
          borderRadius: 0.5,
          border: `1px solid ${ROTATION_COLORS.border.subtle}`,
          overflow: 'hidden',
        }}
      >
        {w > 0 && h > 0 && (
          <svg width={w} height={h} style={{ display: 'block', pointerEvents: 'none' }}>
            <defs>
              <linearGradient id="sr-grad-pos" x1="0" y1="0" x2="0" y2={cy} gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={awayTeamColor} stopOpacity={0.6} />
                <stop offset="100%" stopColor={awayTeamColor} stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="sr-grad-neg" x1="0" y1={h} x2="0" y2={cy} gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={homeTeamColor} stopOpacity={0.6} />
                <stop offset="100%" stopColor={homeTeamColor} stopOpacity={0.05} />
              </linearGradient>
              <clipPath id="sr-clip-upper">
                <rect x={0} y={0} width={w} height={cy} />
              </clipPath>
              <clipPath id="sr-clip-lower">
                <rect x={0} y={cy} width={w} height={cy} />
              </clipPath>
            </defs>

            {gridYPositions.map((gy, i) => (
              <line key={i} x1={0} y1={gy} x2={w} y2={gy}
                stroke="rgba(107, 114, 128, 0.2)" strokeWidth={1} />
            ))}

            <line x1={0} y1={cy} x2={w} y2={cy} stroke="#6B7280" strokeWidth={1} />

            {[720, 1440, 2160, 2880]
              .filter((t) => t <= gameDuration)
              .map((time, idx) => {
                const x = (time / gameDuration) * w;
                return (
                  <g key={time}>
                    <line x1={x} y1={0} x2={x} y2={h}
                      stroke="#6B7280" strokeWidth={1} strokeDasharray="4" />
                    <text x={x} y={12} fill="#6B7280" fontSize={9} textAnchor="middle">
                      Q{idx + 1}
                    </text>
                  </g>
                );
              })}

            <path d={areaPath} fill="url(#sr-grad-pos)" clipPath="url(#sr-clip-upper)" />
            <path d={areaPath} fill="url(#sr-grad-neg)" clipPath="url(#sr-clip-lower)" />

            <path d={strokePath} fill="none" stroke={awayTeamColor} strokeWidth={2}
              clipPath="url(#sr-clip-upper)" />
            <path d={strokePath} fill="none" stroke={homeTeamColor} strokeWidth={2}
              clipPath="url(#sr-clip-lower)" />
          </svg>
        )}

        {/* Interactive overlay + crosshair/tooltip stay as raw divs (direct DOM manipulation) */}
        <div
          style={{ position: 'absolute', inset: 0 }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <div ref={crosshairRef} style={crosshairStyle} />
          <div ref={tooltipRef} style={tooltipStyle} />
        </div>
      </Box>

      <Stack
        direction="row"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 0.5,
          fontSize: '0.65rem',
          fontWeight: 500,
        }}
        style={{ marginTop: 2 }}
      >
        <Typography style={{ fontSize: 'inherit', fontWeight: 'inherit', color: homeTeamColor }}>
          {homeAbbrev} lead
        </Typography>
        {homeLargestLead && (
          <Typography sx={{ color: ROTATION_COLORS.text.secondary, fontSize: 'inherit' }}>
            max +{homeLargestLead}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default ScoreRibbon;
